import type { ResidentDtos } from '@lmc/ts-common'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import type { RotaBuilderFiltersType } from 'screens/Rostering/types'
import { getDateWithoutUTC, YEAR_FIRST_DATE_FORMAT } from '../utils.datetime'
import { useAbsencesList } from './useAbsencesList'
import { useAbsenceTypesList } from './useAbsenceTypesList'
import useCheckGeneratedRota from './useCheckGeneratedRota'
import useRecursiveRevisionsList from './useRecursiveRevisionsList'
import useResidentsList from './useResidentsList'
import useUsersList from './useUsersList'
import {
  getInvalidEventsFromAbsences,
  getTransformedEventResidents,
  getTransformedEventShifts,
  getTransformedEventUsers,
  getTransformedResidentEventsData,
  getTransformedUserEventsData
} from './utils'

type UseRosterProps = {
  selectedDate: Date
  intervalType: 'daily' | 'weekly'
  filterParams: RotaBuilderFiltersType
}

export const useRoster = ({
  selectedDate,
  intervalType,
  filterParams
}: UseRosterProps) => {
  const { revisions, isLoading: isRevisionsLoading } =
    useRecursiveRevisionsList({
      selectedDate,
      intervalType,
      otherFilterParams: {
        status: filterParams.status,
        allocationStatuses: filterParams.allocations,
        teamMembers: filterParams.teamMembers,
        serviceUsers: filterParams.serviceUsers,
        instanceTypes: filterParams.typeOfEvent,
        ...(filterParams.siteShift && { siteShift: true })
      }
    })

  const { isError, error } = useCheckGeneratedRota({ selectedDate })

  const errorCode = isError
    ? (error as { statusCode?: number } | undefined)?.statusCode
    : null

  const { residents, isLoading: isResidentsListLoading } = useResidentsList({
    filters: {
      residentIds: filterParams.serviceUsers,
      date: getDateWithoutUTC(selectedDate, YEAR_FIRST_DATE_FORMAT),
      type: 'Revisions'
    }
  })

  const {
    users,
    isLoading: isUsersListLoading,
    unassignedHours
  } = useUsersList({
    selectedDate,
    type: 'Revisions',
    otherFilterParams: {
      absences: filterParams.absences,
      roles: filterParams.roles,
      overtime: filterParams.overtime,
      userIds: filterParams?.teamMembers
    }
  })

  const date =
    intervalType === 'weekly'
      ? dayjs(selectedDate).startOf('isoWeek').format(YEAR_FIRST_DATE_FORMAT)
      : dayjs(selectedDate).format(YEAR_FIRST_DATE_FORMAT)

  const startDate = dayjs(date).startOf('day').toISOString()

  const endDate =
    intervalType === 'weekly'
      ? dayjs(date).endOf('isoWeek').toISOString()
      : dayjs(date).endOf('day').toISOString()

  const { absences } = useAbsencesList({
    startDate,
    endDate,
    otherFilterParams: { ...(filterParams.siteShift && { siteShift: true }) }
  })

  const { absenceTypes } = useAbsenceTypesList()

  const usersRevisions = useMemo(
    () => getTransformedUserEventsData(revisions),
    [revisions]
  )

  const residentsRevisions = useMemo(
    () => getTransformedResidentEventsData(revisions),
    [revisions]
  )

  const shifts = useMemo(
    () => getTransformedEventShifts(revisions),
    [revisions]
  )

  const isLoading =
    isUsersListLoading || isResidentsListLoading || isRevisionsLoading

  return {
    users: getTransformedEventUsers(users),
    usersRevisions: usersRevisions || [],
    residents: getTransformedEventResidents(
      residents as unknown as ResidentDtos[],
      residentsRevisions
    ),
    residentsRevisions: residentsRevisions || [],
    isRotaBuilderLoading: isLoading,
    invalid: getInvalidEventsFromAbsences(absences, absenceTypes),
    shifts,
    metadata: { unassignedHours },
    errorCode
  }
}

export default useRoster
